class HairQuiz extends HTMLElement {
  constructor() {
    super();
    this.answers = {};
    this.screens = Array.from(this.querySelectorAll('[data-hq-screen]'));
    this.history = [];
    this.loadingDelay = parseInt(this.dataset.loadingDelay, 10) || 2600;
    this.started = false;
    this.emailSubmitted = false;
    this.completed = false;
  }

  // Analytics: every quiz event goes to GA4 (window.gtag, loaded by
  // snippets/google-analytics.liquid when a measurement ID is set) and to
  // Shopify's custom-pixel bus, so either can pick it up. Never throws.
  track(name, params) {
    const payload = Object.assign({ quiz: 'hair_quiz', locale: document.documentElement.lang || '' }, params || {});
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
    } catch (e) {
      /* analytics must never break the quiz */
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) {
      /* ignore */
    }
  }

  // Flat, low-cardinality snapshot of the answers for event parameters.
  answerParams() {
    const out = {};
    Object.keys(this.answers).forEach((step) => {
      out['answer_' + step] = this.values(step).join('|').slice(0, 100);
    });
    return out;
  }

  connectedCallback() {
    this.bindOptions();
    this.bindNav();
    this.bindEmail();
    this.bindResultsActions();
    const start = this.screens.find((s) => s.dataset.hqScreen && s.dataset.hqScreen !== 'loading' && s.dataset.hqScreen !== 'results');
    this.show(start, false);
  }

  screenFor(id) {
    return this.screens.find((s) => s.dataset.hqScreen === id);
  }

  show(screen, pushHistory = true) {
    if (!screen) return;
    this.screens.forEach((s) => {
      s.classList.remove('is-active');
      s.hidden = true;
    });
    if (this.current && pushHistory) this.history.push(this.current);
    this.current = screen;
    screen.hidden = false;
    screen.classList.add('is-active');
    // Only scroll if the top of the quiz has moved out of view (e.g. long
    // results after a short question) — never on ordinary step changes.
    const top = this.getBoundingClientRect().top;
    if (top < 0) {
      window.scrollTo({ top: window.scrollY + top, behavior: 'smooth' });
    }

    const id = screen.dataset.hqScreen;
    if (id !== 'loading' && id !== 'results') {
      this.track('hair_quiz_step', { step: id, step_index: this.screens.indexOf(screen) + 1 });
    }
    if (id === 'loading') {
      window.setTimeout(() => this.show(this.screenFor('results')), this.loadingDelay);
    }
    if (id === 'results') {
      this.renderProfile();
      this.renderPersonalization();
      if (!this.completed) {
        this.completed = true;
        this.track(
          'hair_quiz_complete',
          Object.assign({ with_email: this.emailSubmitted ? 'yes' : 'no' }, this.answerParams())
        );
      }
    }
  }

  values(step) {
    return (this.answers[step] || []).map((a) => a.value);
  }

  has(step, value) {
    return this.values(step).includes(value);
  }

  goBack() {
    const prev = this.history.pop();
    if (prev) {
      this.current = null;
      this.show(prev, false);
    }
  }

  goNext(fromScreen) {
    const next = fromScreen.dataset.hqNext;
    if (next) this.show(this.screenFor(next));
  }

  bindOptions() {
    this.querySelectorAll('.hair-quiz__option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('[data-hq-screen]');
        const step = panel.dataset.hqScreen;
        const multi = panel.dataset.hqMode === 'multi';
        if (!this.started) {
          this.started = true;
          this.track('hair_quiz_start', { first_step: step });
        }

        if (multi) {
          btn.classList.toggle('is-selected');
          this.answers[step] = Array.from(panel.querySelectorAll('.hair-quiz__option.is-selected')).map((b) => ({
            value: b.dataset.value,
            label: b.textContent.trim(),
          }));
          this.updateContinueState(panel);
        } else {
          panel.querySelectorAll('.hair-quiz__option').forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          this.answers[step] = [{ value: btn.dataset.value, label: btn.textContent.trim() }];
          window.setTimeout(() => this.goNext(panel), 220);
        }
      });
    });
  }

  updateContinueState(panel) {
    const cont = panel.querySelector('.hair-quiz__continue');
    if (cont) cont.disabled = !(this.answers[panel.dataset.hqScreen] || []).length;
  }

  bindNav() {
    this.querySelectorAll('.hair-quiz__continue').forEach((btn) => {
      btn.addEventListener('click', () => this.goNext(btn.closest('[data-hq-screen]')));
    });
    this.querySelectorAll('.hair-quiz__back').forEach((btn) => {
      btn.addEventListener('click', () => this.goBack());
    });
    this.querySelectorAll('.hair-quiz__skip').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.track('hair_quiz_email_skip');
        this.show(this.screenFor('loading'));
      });
    });
  }

  bindEmail() {
    const form = this.querySelector('.hair-quiz__email-form');
    if (!form) return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const error = form.querySelector('.hair-quiz__error');
      const email = input.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        error.classList.add('is-visible');
        input.focus();
        return;
      }
      error.classList.remove('is-visible');
      const consentBox = form.querySelector('input[type="checkbox"]');
      const consent = !!(consentBox && consentBox.checked);
      this.emailSubmitted = true;
      this.track('hair_quiz_email_submit', { consent: consent ? 'yes' : 'no' });
      this.track('generate_lead', { method: 'hair_quiz', consent: consent ? 'yes' : 'no' });
      this.submitEmail(form, email);
      this.show(this.screenFor('loading'));
    });
  }

  async submitEmail(form, email) {
    const consentBox = form.querySelector('input[type="checkbox"]');
    const consent = !!(consentBox && consentBox.checked);
    // Primary: the email service creates/tags the customer through the Admin API and
    // sends the routine guide. (Shopify's /contact rejects fetch() posts without a
    // captcha token, so it is only kept as a fallback below.)
    const endpoint = this.dataset.signupEndpoint;
    if (endpoint) {
      const answers = {};
      Object.keys(this.answers).forEach((step) => {
        answers[step] = this.values(step);
      });
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, consent, locale: document.documentElement.lang || '', answers, source: 'hair-quiz' }),
        });
        if (res.ok) return;
      } catch (e) {
        /* fall through to the storefront form */
      }
    }
    const body = new FormData();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[tags]', 'hair-quiz');
    if (consent) body.append('contact[accepts_marketing]', 'true');
    try {
      await fetch('/contact', { method: 'POST', body, headers: { Accept: 'application/json' } });
    } catch (e) {
      /* non-blocking: the quiz proceeds regardless */
    }
  }

  renderProfile() {
    const target = this.querySelector('[data-hq-profile-summary]');
    if (!target || target.dataset.rendered) return;
    const pick = (step) => (this.answers[step] && this.answers[step][0] ? this.answers[step][0].label : '—');
    const template = target.dataset.template || '';
    target.textContent = template
      .replace('[texture]', pick('texture'))
      .replace('[thickness]', pick('thickness'))
      .replace('[length]', pick('length'));
    target.dataset.rendered = 'true';
  }

  get strings() {
    if (!this._strings) {
      const blob = this.querySelector('[data-hq-strings]');
      try {
        // The `t` filter HTML-escapes translations (e.g. "&" -> "&amp;"),
        // so decode entities on each parsed string value.
        const decoder = document.createElement('textarea');
        const decode = (value) => {
          if (typeof value === 'string') {
            decoder.innerHTML = value;
            return decoder.value;
          }
          if (value && typeof value === 'object') {
            Object.keys(value).forEach((k) => {
              value[k] = decode(value[k]);
            });
          }
          return value;
        };
        this._strings = decode(JSON.parse(blob ? blob.textContent : '{}'));
      } catch (e) {
        this._strings = {};
      }
    }
    return this._strings;
  }

  renderPersonalization() {
    if (this.dataset.personalized) return;
    this.dataset.personalized = 'true';
    this.renderConcernChips();
    this.renderRoutine();
    this.renderRecommendations();
  }

  renderConcernChips() {
    const wrap = this.querySelector('[data-hq-profile-goals]');
    if (!wrap) return;
    const chips = (this.answers.goals || []).map((g) => g.label);
    const scalp = (this.answers.scalp || [])[0];
    if (scalp && scalp.value !== 'none') chips.push(scalp.label);
    if (!chips.length) return;
    const title = this.querySelector('[data-hq-concerns-title]');
    if (title) title.hidden = false;
    chips.forEach((label) => {
      const chip = document.createElement('span');
      chip.className = 'hair-quiz__chip';
      chip.textContent = label;
      wrap.appendChild(chip);
    });
  }

  renderRoutine() {
    const list = this.querySelector('[data-hq-routine]');
    const s = this.strings.routine;
    if (!list || !s) return;

    const washFreq = this.values('wash_frequency')[0] || 'weekly';
    let wash = s['wash_' + washFreq] || s.wash_weekly;
    if (this.has('color_treated', 'yes')) wash += ' ' + s.wash_color_note;

    const treat = this.has('goals', 'damage_repair') ? s.treat_damage : s.treat_hydration;

    let care = s.care_default;
    if (this.has('goals', 'growth')) care = s.care_growth;
    else if (this.has('goals', 'scalp_balance') || !this.has('scalp', 'none')) care = s.care_scalp;

    let style = s.style_default;
    if (this.has('goals', 'curl_definition')) style = s.style_curls;
    else if (this.has('goals', 'frizz_control')) style = s.style_frizz;

    [
      { label: s.wash_label, text: wash },
      { label: s.treat_label, text: treat },
      { label: s.care_label, text: care },
      { label: s.style_label, text: style },
    ].forEach((step) => {
      const li = document.createElement('li');
      const strong = document.createElement('strong');
      strong.textContent = step.label + ': ';
      li.appendChild(strong);
      li.appendChild(document.createTextNode(step.text));
      list.appendChild(li);
    });
  }

  renderRecommendations() {
    const reasons = this.strings.reasons;
    if (!reasons) return;

    const reasonFor = (role) => {
      if (role === 'mask') {
        if (this.has('goals', 'damage_repair')) return reasons.mask_damage;
        if (this.has('color_treated', 'yes')) return reasons.mask_color;
        return reasons.mask_default;
      }
      if (role === 'serum') {
        if (this.has('goals', 'growth')) return reasons.serum_growth;
        if (this.has('goals', 'scalp_balance') || !this.has('scalp', 'none')) return reasons.serum_scalp;
        return reasons.serum_default;
      }
      if (role === 'cream') {
        if (this.has('goals', 'curl_definition')) return reasons.cream_curls;
        if (this.has('goals', 'frizz_control')) return reasons.cream_frizz;
        return reasons.cream_default;
      }
      if (role === 'butter') return reasons.butter_default;
      return '';
    };

    const scoreFor = (role) => {
      let score = 0;
      if (role === 'mask') {
        score = 3;
        if (this.has('goals', 'damage_repair') || this.has('goals', 'hydration')) score += 3;
        if (this.has('color_treated', 'yes')) score += 1;
      }
      if (role === 'serum') {
        score = 2;
        if (this.has('goals', 'growth')) score += 3;
        if (this.has('goals', 'scalp_balance') || !this.has('scalp', 'none')) score += 2;
      }
      if (role === 'cream') {
        score = 1;
        if (this.has('goals', 'curl_definition')) score += 3;
        if (this.has('goals', 'frizz_control')) score += 2;
        if (this.has('texture', 'curly') || this.has('texture', 'coily')) score += 1;
      }
      if (role === 'butter') {
        score = 1;
        if (this.has('goals', 'hydration')) score += 2;
        if (this.has('scalp', 'dryness')) score += 1;
        if (this.has('texture', 'curly') || this.has('texture', 'coily')) score += 1;
      }
      return score;
    };

    this.querySelectorAll('.hair-quiz__products').forEach((grid) => {
      const cards = Array.from(grid.querySelectorAll('[data-hq-role]'));
      cards
        .map((card) => ({ card, score: scoreFor(card.dataset.hqRole) }))
        .sort((a, b) => b.score - a.score)
        .forEach(({ card }) => {
          const reason = reasonFor(card.dataset.hqRole);
          const slot = card.querySelector('[data-hq-reason]');
          if (slot && reason) {
            slot.textContent = reason;
            slot.hidden = false;
          }
          grid.appendChild(card);
        });
    });
  }

  bindResultsActions() {
    this.querySelectorAll('[data-hq-copy-code]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.dataset.code || '';
        this.track('hair_quiz_copy_code', { code });
        try {
          await navigator.clipboard.writeText(code);
        } catch (e) {
          /* clipboard unavailable */
        }
        const original = btn.textContent;
        btn.textContent = btn.dataset.copiedLabel || 'Copied!';
        window.setTimeout(() => {
          btn.textContent = original;
        }, 1800);
      });
    });

    this.querySelectorAll('[data-hq-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.track('hair_quiz_add_to_cart', { items: 1, variant_id: btn.dataset.variantId || '', source: 'single' });
        this.addToCart([{ id: btn.dataset.variantId, quantity: 1 }], btn);
      });
    });

    const addAll = this.querySelector('[data-hq-add-all]');
    if (addAll) {
      const ids = (addAll.dataset.variantIds || '').split(',').filter(Boolean);
      addAll.addEventListener('click', () => {
        this.track('hair_quiz_add_to_cart', { items: ids.length, source: 'add_all' });
        this.addToCart(ids.map((id) => ({ id, quantity: 1 })), addAll);
      });
    }
  }

  async addToCart(items, btn) {
    if (!items.length || !items[0].id) return;
    btn.disabled = true;
    // Dawn renders either <cart-drawer> or <cart-notification> depending on
    // the "cart type" theme setting; both expose the same section-render API.
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const payload = { items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }
      if (cart && typeof cart.renderContents === 'function' && data.sections) {
        // Multi-item responses carry the lines in `items` and no top-level
        // `key`; the notification uses `key` to pick the line to display.
        if (!data.key && Array.isArray(data.items) && data.items.length) {
          data.key = data.items[0].key;
          data.id = data.items[0].id;
        }
        cart.renderContents(data);
      } else {
        window.location.href = '/cart';
      }
    } catch (e) {
      const original = btn.textContent;
      btn.textContent = (e && e.message) || 'Could not add to cart';
      window.setTimeout(() => {
        btn.textContent = original;
      }, 3000);
    } finally {
      btn.disabled = false;
    }
  }

}

customElements.define('hair-quiz', HairQuiz);
